import { formatDataCompletenessGapCheckDate } from '@/src/utils/dataCompletenessDate.utils'
import { Box, Divider } from '@chakra-ui/react'
import { FlexCol, FlexRow } from '@components'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BaseText } from '../BaseText'
import { DataCompletenessItemsList } from './DataCompletenessItemsList'
import { DataCompletenessLegendItem } from './DataCompletenessLegendItem'
import type { DataCompletenessScope } from './dataCompleteness.mapper'
import {
  type IDataCompletenessItem,
  completenessIndicatorLayout,
  filterIncompleteDataCompletenessItems,
  sortDataCompletenessItemsByPercentage,
} from './dataCompletenessIndicator.utils'

interface DataCompletenessIndicatorProps {
  items: IDataCompletenessItem[]
  timeframe: string
  description?: string
  onlyShowIncompleteItems?: boolean
  scope: DataCompletenessScope
}

export const DataCompletenessIndicatorContent = ({
  items,
  timeframe,
  description,
  onlyShowIncompleteItems = false,
  scope,
}: DataCompletenessIndicatorProps) => {
  const { t } = useTranslation()
  const gapCheckDate = useMemo(() => formatDataCompletenessGapCheckDate(), [])
  const displayItems = useMemo(() => {
    const filteredItems = onlyShowIncompleteItems ? filterIncompleteDataCompletenessItems(items) : items
    return sortDataCompletenessItemsByPercentage(filteredItems)
  }, [items, onlyShowIncompleteItems])
  const hasIntervalData = displayItems.some(item => (item.data?.length ?? 0) > 0)

  if (displayItems.length === 0) {
    return (
      <Box width={'322px'} bg='background' borderColor={'border'} borderWidth={'1px'} borderRadius={'8px'} p={'12px'}>
        <BaseText fontSize={'14px'} color={'foreground'}>
          {t('components.data_completeness_indicator.empty_state')}
        </BaseText>
      </Box>
    )
  }

  return (
    <FlexCol
      width={`${completenessIndicatorLayout.containerWidthPx}px`}
      maxHeight={'500px'}
      px={`${completenessIndicatorLayout.containerHorizontalPaddingPx}px`}
      py={`${completenessIndicatorLayout.containerVerticalPaddingPx}px`}
      bg='background'
      borderColor={'border'}
      borderWidth={'1px'}
      borderRadius={'8px'}
    >
      <DataCompletenessItemsList items={displayItems} scope={scope} />
      <FlexCol flex={'0 0 auto'}>
        {hasIntervalData && (
          <>
            <Divider width={'100%'} my={'4px'} />
            <FlexCol gap={'4px'}>
              <BaseText fontSize={'12px'} color={'secondaryForeground'}>
                {t('components.data_completeness_indicator.interval')}
              </BaseText>
              <FlexRow alignItems={'center'} gap={'16px'}>
                <DataCompletenessLegendItem type='complete' text={t('components.data_completeness_indicator.complete')} />
                <DataCompletenessLegendItem type='partial' text={t('components.data_completeness_indicator.partial')} />
                <DataCompletenessLegendItem type='missing' text={t('components.data_completeness_indicator.missing')} />
              </FlexRow>
            </FlexCol>
          </>
        )}
        <Divider width={'100%'} my={'4px'} />
        <FlexCol justifyContent={'center'}>
          <BaseText fontSize={'12px'} color={'secondaryForeground'}>
            {t('components.data_completeness_indicator.timeframe', { timeframe })}
          </BaseText>
          <BaseText fontSize={'12px'} color={'secondaryForeground'}>
            {t('components.data_completeness_indicator.gap_check_schedule', { date: gapCheckDate })}
          </BaseText>
          {description && (
            <BaseText fontSize={'12px'} color={'secondaryForeground'} whiteSpace='pre-line'>
              {description}
            </BaseText>
          )}
        </FlexCol>
      </FlexCol>
    </FlexCol>
  )
}
